export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Bounding box of a rotated rectangle (used when rotation ≠ 0).
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

function configureHighQuality(ctx) {
  if (!ctx) {
    return;
  }
  ctx.imageSmoothingEnabled = true;
  if ('imageSmoothingQuality' in ctx) {
    ctx.imageSmoothingQuality = 'high';
  }
}

/** JPEG keeps file size reasonable; quality 0.92 stays sharp at 2× banner size. */
const BG_CROP_MIME = 'image/jpeg';
const BG_CROP_QUALITY = 0.92;

/**
 * Crop (and optionally resize) an image for use as a canvas background.
 *
 * Uses drawImage (not the old safeArea + getImageData/putImageData path).
 * When `resize` is set, always scales to that size so uploads don't stay at
 * multi‑megapixel screenshot resolution (which blew export size to ~3MB).
 */
export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  resize,
) {
  if (!pixelCrop?.width || !pixelCrop?.height) {
    return undefined;
  }

  const image = await createImage(imageSrc);
  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d');
  if (!croppedCtx) {
    return undefined;
  }

  configureHighQuality(croppedCtx);

  const cropX = Math.max(0, Math.min(Math.round(pixelCrop.x), image.width - 1));
  const cropY = Math.max(0, Math.min(Math.round(pixelCrop.y), image.height - 1));
  const cropW = Math.max(
    1,
    Math.min(Math.round(pixelCrop.width), image.width - cropX),
  );
  const cropH = Math.max(
    1,
    Math.min(Math.round(pixelCrop.height), image.height - cropY),
  );

  if (!rotation) {
    croppedCanvas.width = cropW;
    croppedCanvas.height = cropH;
    croppedCtx.drawImage(
      image,
      cropX,
      cropY,
      cropW,
      cropH,
      0,
      0,
      cropW,
      cropH,
    );
  } else {
    const rotRad = getRadianAngle(rotation);
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation,
    );

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return undefined;
    }

    canvas.width = Math.max(1, Math.round(bBoxWidth));
    canvas.height = Math.max(1, Math.round(bBoxHeight));
    configureHighQuality(ctx);

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rotRad);
    ctx.translate(-image.width / 2, -image.height / 2);
    ctx.drawImage(image, 0, 0);

    croppedCanvas.width = cropW;
    croppedCanvas.height = cropH;
    croppedCtx.drawImage(
      canvas,
      cropX,
      cropY,
      cropW,
      cropH,
      0,
      0,
      cropW,
      cropH,
    );
  }

  if (resize?.width && resize?.height) {
    const outW = Math.max(1, Math.round(resize.width));
    const outH = Math.max(1, Math.round(resize.height));
    const oc = document.createElement('canvas');
    const octx = oc.getContext('2d');
    if (!octx) {
      return croppedCanvas.toDataURL(BG_CROP_MIME, BG_CROP_QUALITY);
    }
    oc.width = outW;
    oc.height = outH;
    configureHighQuality(octx);
    octx.drawImage(croppedCanvas, 0, 0, cropW, cropH, 0, 0, outW, outH);
    return oc.toDataURL(BG_CROP_MIME, BG_CROP_QUALITY);
  }

  return croppedCanvas.toDataURL(BG_CROP_MIME, BG_CROP_QUALITY);
}
